import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { trackEvent } from "../analytics";
import "./Blog.css";

/**
 * 체크리스트 다운로드 버튼 + 이메일/동의 게이트 모달.
 *
 * 사용:
 *   <ChecklistDownload
 *     checklistTitle="작업 전 TBM 기록 체크리스트"
 *     pdf="/checklists/tbm-checklist.pdf"
 *     fileName="현장통_TBM기록_체크리스트.pdf"
 *     className="sm-cta-btn primary"
 *     label="무료 체크리스트 받기 →"
 *   />
 *
 * 이메일 발송(리드 수집)은 emailjs를 사용합니다. 랜딩 문의폼과 동일한 서비스/공개키를 재사용합니다.
 * 깔끔한 리드 메일을 받으려면 emailjs 대시보드에 아래 변수를 쓰는 전용 템플릿을 만들고
 * CHECKLIST_TEMPLATE_ID를 교체하세요: {{checklist}}, {{user_email}}, {{marketing}}, {{page}}
 */
const EMAILJS_SERVICE_ID = "service_aw37t4d";
const EMAILJS_PUBLIC_KEY = "q-o1WnQB6dOaQAOlQ";
// TODO: 체크리스트 전용 템플릿 생성 후 교체 권장 (현재는 문의폼 템플릿 재사용)
const CHECKLIST_TEMPLATE_ID = "template_hly2d66";
const CHECKLIST_RECIPIENT_EMAIL = "admin@hongtong.kr";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ChecklistDownload({ checklistTitle, pdf, fileName, className, label }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [agreeRequired, setAgreeRequired] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | done
  const [error, setError] = useState("");

  const reset = () => {
    setEmail("");
    setAgreeRequired(false);
    setAgreeMarketing(false);
    setStatus("idle");
    setError("");
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const triggerDownload = () => {
    const url = (process.env.PUBLIC_URL || "") + pdf;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError("올바른 이메일 주소를 입력해주세요.");
      return;
    }
    if (!agreeRequired) {
      setError("개인정보 수집·이용에 동의해주세요.");
      return;
    }
    setError("");
    setStatus("sending");

    const params = {
      checklist: checklistTitle,
      user_email: email,
      email: CHECKLIST_RECIPIENT_EMAIL,
      marketing: agreeMarketing ? "동의" : "미동의",
      page: typeof window !== "undefined" ? window.location.pathname : "",
      message: `[체크리스트 신청] ${checklistTitle} / 이메일: ${email} / 마케팅수신: ${
        agreeMarketing ? "동의" : "미동의"
      }`,
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        CHECKLIST_TEMPLATE_ID,
        params,
        EMAILJS_PUBLIC_KEY
      );
    } catch (err) {
      // 리드 발송 실패해도 사용자 다운로드는 막지 않습니다.
      console.error("checklist lead email failed:", err);
    }

    trackEvent("checklist_download", {
      checklist: checklistTitle || "unknown_checklist",
      marketing: agreeMarketing ? "yes" : "no",
      page: window.location.pathname,
    });
    triggerDownload();
    setStatus("done");
  };

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
      >
        {label}
      </button>

      {open && (
        <div className="cl-modal-overlay" onClick={close}>
          <div
            className="cl-modal"
            role="dialog"
            aria-modal="true"
            aria-label="체크리스트 다운로드"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="cl-modal-close" onClick={close} aria-label="닫기">
              ×
            </button>

            {status === "done" ? (
              <div className="cl-modal-done">
                <div className="cl-modal-done-icon">✓</div>
                <h3>다운로드가 시작되었습니다</h3>
                <p>
                  <strong>{checklistTitle}</strong> 자료를 받으셨습니다.
                  <br />
                  다운로드가 자동으로 시작되지 않으면 아래 버튼을 눌러주세요.
                </p>
                <button className="cl-btn-primary" onClick={triggerDownload}>
                  다시 다운로드
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="cl-modal-head">
                  <span className="cl-modal-tag">무료 자료</span>
                  <h3>{checklistTitle}</h3>
                  <p>이메일을 입력하시면 PDF 자료를 바로 받아보실 수 있습니다.</p>
                </div>

                <label className="cl-field">
                  <span>이메일</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    autoFocus
                  />
                </label>

                <label className="cl-consent">
                  <input
                    type="checkbox"
                    checked={agreeRequired}
                    onChange={(e) => setAgreeRequired(e.target.checked)}
                  />
                  <span>
                    <b>[필수] 개인정보 수집·이용 동의</b>
                    <br />
                    수집 목적: 체크리스트 다운로드 제공 · 수집 항목: 이메일 주소 · 보유·이용
                    기간: 수집일로부터 1년 또는 삭제 요청 시까지. 동의하지 않을 권리가 있으며,
                    미동의 시 다운로드가 제한될 수 있습니다.
                  </span>
                </label>

                <label className="cl-consent">
                  <input
                    type="checkbox"
                    checked={agreeMarketing}
                    onChange={(e) => setAgreeMarketing(e.target.checked)}
                  />
                  <span>
                    <b>[선택] 마케팅 정보 수신 동의</b>
                    <br />
                    현장통 서비스 소식·자료·이벤트·상담 안내 메일 수신. 동의하지 않아도
                    다운로드는 가능합니다.
                  </span>
                </label>

                {error && <div className="cl-error">{error}</div>}

                <button
                  type="submit"
                  className="cl-btn-primary"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "처리 중…" : "동의하고 다운로드"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ChecklistDownload;
